import type { CombatVisualEvent } from '../../../shared/game/combat-types.js';
import type { OverheadMessage } from '../overhead-messages.js';

const notices = {
  interpose: { text: 'INTERPOSE', color: 0x73cfff },
  assess: { text: 'ASSESS', color: 0xc5a4ff },
  anticipate: { text: 'ANTICIPATE DANGER', color: 0xc9b37e },
  mark: { text: 'HUNTER’S MARK', color: 0xffa24d },
  command: { text: 'COORDINATED STRIKE', color: 0x9fd0ff },
  initiative: { text: 'INITIATIVE!', color: 0xffe08a },
  condition: { text: 'CONDITION', color: 0xe6a1ff },
  teleport: { text: 'SPARK STEP', color: 0xc5a4ff },
  ability: { text: '', color: 0xffa86b },
} satisfies Partial<Record<CombatVisualEvent['kind'], { text: string; color: number }>>;

/** Abilities whose caption is held over the monster while their state lasts, rather than shown once. */
export const SUSTAINED_ABILITIES: ReadonlySet<string> = new Set(['charge']);
/** The caption a unit holds while it keeps an earned CHAAARGE! (until its next attack roll spends it). */
export function chargeMessage(unitId: string): OverheadMessage {
  return { text: 'CHAAARGE!', color: notices.ability.color, style: 'notice', unitId, point: { x: 0, y: 2.3, z: 0 }, clearance: .65, effects: ['pop', 'pulse'] };
}

const RADIANT_GOLD = 0xffd56a;
/** The radiant damage a Judgment blow carried, in gold beside the blow's own number. */
export function judgmentMessage(event: CombatVisualEvent): OverheadMessage | undefined {
  if (event.kind !== 'impact' || !event.judgment) return undefined;
  return { text: `−${event.judgment}`, color: RADIANT_GOLD, style: 'number', unitId: event.targetId, point: { ...event.to, y: 2.75 }, clearance: .3, duration: 1500, effects: ['rise', 'pop'] };
}

/** One presentation policy for every authoritative combat caption, including custom labels. */
export function combatMessage(event: CombatVisualEvent, actorIsHero: boolean): OverheadMessage | undefined {
  if (event.kind === 'strike' || event.kind === 'arc') return undefined;
  if (event.kind === 'ability' && SUSTAINED_ABILITIES.has(event.abilityId ?? '')) return undefined;
  // Sneak Attack slams in over the Cheat and bleeds; its animation is the caption's own (`overhead-message--sneak`).
  if (event.kind === 'ability' && event.abilityId === 'sneak-attack') {
    return { text: 'SNEAK ATTACK', color: 0xeadfca, style: 'notice', flourish: 'sneak', unitId: event.actorId, point: { ...event.from, y: 2.3 }, clearance: .75, duration: 1800, effects: ['rise'] };
  }
  // A Rage and the dice thrown against a blow burn in blood red and slam in with the shake of the moment.
  if (event.kind === 'ability' && (event.abilityId === 'rage' || event.abilityId === 'that-all-you-got')) {
    return { text: event.label ?? 'RAGE', color: 0xe8553a, style: 'notice', unitId: event.actorId, point: { ...event.from, y: 2.3 }, clearance: .75, duration: 1800, effects: ['rise', 'pop', 'shake'] };
  }
  // Judgment Dice set aside: a gold word over the Oathsworn, quieter than a Rage.
  if (event.kind === 'ability' && event.abilityId === 'radiant-judgment') {
    return { text: event.label ?? 'JUDGMENT', color: RADIANT_GOLD, style: 'notice', unitId: event.actorId, point: { ...event.from, y: 2.3 }, clearance: .65, duration: 1500, effects: ['rise', 'pop'] };
  }
  // Hiding, running and being spotted show on the hero (the shadow veil gathering and tearing, the dust kicked up), not as words.
  if (event.abilityId === 'hide' || event.abilityId === 'run' || event.abilityId === 'spotted') return undefined;
  // Commanded Shadows show as the threads to their foe (`shadowmancer-effects.ts`).
  if (event.abilityId === 'command-shadows') return undefined;
  const notice = event.kind in notices ? notices[event.kind as keyof typeof notices] : undefined;
  // A Judgment blow's radiant share has its own number (`judgmentMessage`); this one keeps the rest.
  const dealt = event.kind === 'impact' && event.judgment ? event.damage - event.judgment : event.damage;
  if (dealt <= 0 && event.judgment && !event.critical && !event.label) return undefined;
  const text = event.label ?? notice?.text ?? (event.kind === 'heal' ? `+${event.damage} HP` : event.kind === 'block' ? `BLOCK −${event.damage}` : event.kind === 'miss' ? 'MISS' : `${event.critical ? 'CRITICAL ' : ''}−${dealt}`);
  const color = notice?.color ?? (event.kind === 'heal' ? 0x7ee08a : event.kind === 'block' ? 0x73cfff : event.critical ? 0xffdf71 : actorIsHero ? 0xffc675 : 0xff715d);
  const critical = event.kind === 'impact' && event.critical && !event.label;
  return {
    text, color, style: notice || event.label ? 'notice' : 'number', ...critical ? { flourish: 'critical' as const } : {},
    unitId: event.kind === 'ability' ? event.actorId : event.targetId, point: { ...(event.kind === 'ability' ? event.from : event.to), y: 2.3 }, clearance: notice ? .65 : .3,
    duration: event.kind === 'initiative' ? 4000 : critical ? 1700 : 1400,
    // A critical's slam and burst are its flourish's own; it keeps only the shake.
    effects: critical ? ['rise', 'shake'] : ['rise', 'pop', ...(event.kind === 'heal' ? ['pulse' as const] : [])],
  };
}
